import { Link, Outlet, useNavigate, useRouteContext } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { api, mediaQuery, overviewQuery, pagesQuery, peopleQuery, worksQuery } from "../lib/api";
import { uploads } from "../lib/uploads";
import { initials } from "../lib/format";
import { CommandMenu, type Command } from "./CommandMenu";
import { DropZone } from "./DropZone";
import { UploadTray } from "./UploadTray";

const NAV = [
  { to: "/pages", label: "Pages" },
  { to: "/works", label: "Works" },
  { to: "/media", label: "Media" },
  { to: "/people", label: "People" },
  { to: "/inbox", label: "Inbox" },
] as const;

export function Shell() {
  const { user } = useRouteContext({ from: "/_app" });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const overview = useQuery(overviewQuery);
  // Refresh the media library whenever an upload starts or finishes, from any screen.
  useEffect(() => uploads.onMediaChanged(() => void qc.invalidateQueries({ queryKey: mediaQuery.queryKey })), [qc]);
  const signOut = useMutation({
    mutationFn: () => api("/auth/logout", { method: "POST" }),
    onSettled: async () => {
      qc.clear();
      await navigate({ to: "/login", search: {} });
    },
  });
  // ⌘K can open any work, person or page by name, besides the built-in actions.
  const works = useQuery(worksQuery);
  const people = useQuery(peopleQuery);
  const pages = useQuery(pagesQuery);
  const createWork = useMutation({
    mutationFn: () => api<{ id: string }>("/studio/works", { method: "POST", json: { title: "Untitled work" } }),
    onSuccess: ({ id }) => void navigate({ to: "/works/$id", params: { id } }),
  });
  const extra = useMemo<Command[]>(
    () => [
      { id: "new-work", label: "New work", run: () => createWork.mutate() },
      { id: "upload", label: "Upload media", run: () => uploads.pick() },
      ...(works.data ?? []).map((w) => ({ id: `w-${w.id}`, label: `Work: ${w.title}`, hint: { draft: "Draft", review: "In review", published: "Live", archived: "Archived" }[w.status], run: () => void navigate({ to: "/works/$id", params: { id: w.id } }) })),
      ...(people.data ?? []).map((p) => ({ id: `p-${p.id}`, label: `Person: ${p.name}`, run: () => void navigate({ to: "/people/$id", params: { id: p.id } }) })),
      ...(pages.data ?? []).map((p) => ({ id: `pg-${p.id}`, label: `Page: ${p.title.en ?? p.slug}`, run: () => void navigate({ to: "/pages/$slug", params: { slug: p.slug } }) })),
      { id: "signout", label: "Sign out", run: () => signOut.mutate() },
    ],
    [signOut, navigate, createWork, works.data, people.data, pages.data],
  );

  return (
    <div className="app">
      <a href="#main" className="skip">Skip to content</a>
      <aside className="side">
        <div className="brand">
          <b>Manos</b>
          <span>Studio</span>
        </div>
        <CommandMenu extra={extra} />
        <nav className="nav" aria-label="Sections">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="nav-i" activeProps={{ "aria-current": "page" }}>
              <span>{n.label}</span>
              {n.to === "/inbox" && !!overview.data?.unread && <small className="badge">{overview.data.unread}</small>}
            </Link>
          ))}
        </nav>
        <div className="grow" />
        <Link to="/settings" className="nav-i" activeProps={{ "aria-current": "page" }}>Categories &amp; team</Link>
        <div className="me">
          {user.avatarUrl ? <img src={user.avatarUrl} alt="" className="av" referrerPolicy="no-referrer" /> : <span className="av">{initials(user.name ?? user.email)}</span>}
          <div className="me-text">
            <span>{user.name ?? user.email}</span>
            <small>{user.role}</small>
          </div>
          <button className="btn ghost sm" onClick={() => signOut.mutate()} disabled={signOut.isPending}>Sign out</button>
        </div>
      </aside>
      <main id="main" className="main">
        <Outlet />
      </main>
      <DropZone />
      <UploadTray />
    </div>
  );
}

export function PageHeader({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <header className="topbar">
      <h1>{title}</h1>
      <div className="topbar-actions">{children}</div>
    </header>
  );
}
